import React, { useMemo, useRef, useState } from "react";
import { Select, Spin } from "antd";
import debounce from "lodash/debounce";
import { WeatherLocation } from "./types";

const { Option } = Select;

type GeocodingResult = {
  id?: number;
  name: string;
  country: string;
  country_code?: string;
  admin1?: string;
  latitude: number;
  longitude: number;
  timezone?: string;
};

type Props = {
  value?: WeatherLocation;
  onChange?: (value?: WeatherLocation) => void;
};

const toLocation = (item: GeocodingResult): WeatherLocation => ({
  id: item.id ? String(item.id) : `${item.latitude},${item.longitude}`,
  name: item.name,
  admin1: item.admin1,
  country: item.country,
  countryCode: item.country_code,
  latitude: item.latitude,
  longitude: item.longitude,
  timezone: item.timezone,
});

const formatLabel = (location: WeatherLocation) =>
  [location.name, location.admin1, location.country].filter(Boolean).join(", ");

const CitySearchSelect: React.FC<Props> = ({ value, onChange }) => {
  const [fetching, setFetching] = useState(false);
  const [options, setOptions] = useState<WeatherLocation[]>([]);
  const fetchRef = useRef(0);

  const debounceFetcher = useMemo(() => {
    const loadOptions = async (searchValue: string) => {
      fetchRef.current += 1;
      const fetchId = fetchRef.current;
      setOptions([]);
      setFetching(true);

      if (!searchValue.trim()) {
        setFetching(false);
        return;
      }

      try {
        const url = `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(searchValue)}&count=5&language=en&format=json`;
        const response = await fetch(url);
        const body = await response.json();

        if (fetchId !== fetchRef.current) return;

        const items = Array.isArray(body.results)
          ? body.results.map(toLocation)
          : [];

        setOptions(items);
      } finally {
        if (fetchId === fetchRef.current) {
          setFetching(false);
        }
      }
    };

    return debounce(loadOptions, 400);
  }, []);

  return (
    <Select
      showSearch
      value={value ? value.id : undefined}
      filterOption={false}
      notFoundContent={fetching ? <Spin size="small" /> : null}
      onSearch={debounceFetcher}
      onChange={(selectedId) => {
        const selected = options.find((option) => option.id === selectedId) ?? value;
        onChange?.(selected);
      }}
      placeholder="Search any city worldwide"
      style={{ display: "block" }}
      optionLabelProp="label"
      allowClear
      onClear={() => onChange?.(undefined)}
    >
      {value && !options.some((option) => option.id === value.id) ? (
        <Option key={value.id} value={value.id} label={formatLabel(value)}>
          {formatLabel(value)}
        </Option>
      ) : null}
      {options.map((option) => (
        <Option key={option.id} value={option.id} label={formatLabel(option)}>
          {formatLabel(option)}
        </Option>
      ))}
    </Select>
  );
};

export default CitySearchSelect;
